import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import PlusSquare from '@untitled-ui/icons-react/build/esm/PlusSquare';

import { Spin } from 'antd';
import { useGetEntityRolesQuery } from 'api/EntitiesApiSlice';
import { BaseButton, SearchInput } from 'components';
import { DeleteButton } from 'components/Buttons/DeleteButton';
import { Address } from 'components/Inputs/AddressInput';
import { GetPersonModel } from 'solstice-types';
import { useDebounce } from 'use-debounce';
import { EntityRole, RoleOption, UserModel } from 'utils';
import { DEFAULT_COUNTRY_VALUE } from 'utils/countries';
import { AddNewIndividualForm } from './AddNewIndividualForm';
import { SelectedIndividual } from './SelectedIndividual';

interface AddNewAssociatedPartyContentProps {
  searchIndividual: (searchTerm: any) => void;
  data: GetPersonModel[];
  isLoading?: boolean;
  selectedIndividual: UserModel | null;
  setSelectedIndividual: (user: UserModel | null) => void;
}

export const AddNewAssociatedPartyContent = ({
  searchIndividual,
  data,
  isLoading,
  selectedIndividual,
  setSelectedIndividual,
}: AddNewAssociatedPartyContentProps) => {
  const { data: roleOptionsData } = useGetEntityRolesQuery({ type: 'Group' });

  const roleOptions: RoleOption[] = roleOptionsData
    ? roleOptionsData
        .map(
          (item: EntityRole): RoleOption => ({
            value: item.id || '',
            label: item.name || '',
          })
        )
        .sort((a, b) => a.label.localeCompare(b.label))
    : [];

  const filteredRoles = roleOptions.filter((role) => role.label !== 'Creator');

  const {
    control,
    formState: { errors },
    reset,
    getValues,
    setValue,
  } = useFormContext();

  const { fields, append } = useFieldArray({
    control,
    name: 'newAssociatedParty',
  });

  const handleAddNewIndividual = () => {
    setSearchQuery('');
    append({
      firstName: '',
      lastName: '',
      middleName: '',
      dateOfbirth: '',
      email: '',
      mobilePhone: '',
      relationship: '',
      country: DEFAULT_COUNTRY_VALUE,
      postcode: '',
      state: '',
      streetName: '',
      streetNumber: '',
      streetType: '',
      suburb: '',
    });
  };
  const handleRemoveIndividual = (index: number) => {
    const currentIndividuals = getValues('newAssociatedParty') || [];
    const updatedIndividuals = currentIndividuals.filter((_: any, i: number) => i !== index);
    setValue('newAssociatedParty', updatedIndividuals);

    const updatedFields = Object.entries(getValues()).reduce<Record<string, any>>(
      (acc, [key, value]) => {
        const match = key.match(/_(\d+)$/);

        if (match) {
          const fieldIndex = Number(match[1]);

          if (fieldIndex === index) {
            return acc;
          }

          if (fieldIndex !== 100) {
            const newIndex = fieldIndex > index ? fieldIndex - 1 : fieldIndex;
            const adjustedKey = key.replace(/_(\d+)$/, `_${newIndex}`);
            acc[adjustedKey] = value;
          } else {
            acc[key] = value;
          }
        } else {
          acc[key] = value;
        }

        return acc;
      },
      {}
    );

    reset(updatedFields);
  };
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isExistingVisible, setIsExistingVisible] = useState<boolean>(false);
  const [relationshipValue, setRelationshipValue] = useState<string>('');
  const [debouncedQuery] = useDebounce(searchQuery, 800);

  const searchOptions = isLoading
    ? [
        {
          value: 'loading',
          label: (
            <div className="flex items-center justify-center">
              <Spin size="small" />
              <span className="ml-2">Loading...</span>
            </div>
          ),
        },
      ]
    : searchQuery && data?.length > 0
      ? data.map((user: UserModel) => ({
          value: user.id,
          label: <SelectedIndividual user={user} searchQuery={searchQuery} areOptionsVisible />,
        }))
      : searchQuery && data?.length === 0
        ? [
            {
              value: 'no-result',
              label: <div className="text-gray-500 font-medium">No results found.</div>,
            },
          ]
        : [];

  useEffect(() => {
    const fetchData = async () => {
      if (debouncedQuery) {
        try {
          await searchIndividual({ searchTerm: debouncedQuery });
        } catch {
          //
        }
      }
    };

    fetchData();
  }, [debouncedQuery, searchIndividual]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const handleSelect = (value: string) => {
    if (value === 'no-result') return;
    setIsExistingVisible(true);
    const selectedItem = data?.find((item) => item.id === value);

    if (selectedItem) {
      setSelectedIndividual({ ...selectedItem, relationship: relationshipValue });
      setSearchQuery('');
    }
  };

  const handleSelectRelationship = (value: string) => {
    setRelationshipValue(value);
    setSelectedIndividual({ ...selectedIndividual, relationship: value });
  };

  const handleSelectAddress = (value: Address) => {
    setSelectedIndividual({ ...selectedIndividual, ...value });
  };

  const handleSelectDob = (value: Date) => {
    setSelectedIndividual({ ...selectedIndividual, ...value });
  };

  const handleSelectedIndividual = () => {
    setIsExistingVisible(false);
    setSearchQuery('');
    setSelectedIndividual(null);
  };

  return (
    <>
      <span className="text-gray-600">
        Is this individual related to any associated party? You can search for an existing individual or add a new one.
      </span>

      <SearchInput
        placeholder="Search for existing individual from previous KYC verifications."
        onSearch={handleSearch}
        onSelect={handleSelect}
        value={searchQuery}
        options={searchOptions}
      />

      {selectedIndividual && (
        <SelectedIndividual
          user={selectedIndividual}
          searchQuery={searchQuery}
          isExistingVisible={isExistingVisible}
          handleDisplayInputs={handleSelectedIndividual}
          isStatusVisible={false}
          isSelectVisible
          selectOptions={filteredRoles}
          onSelect={handleSelectRelationship}
          handleAddress={handleSelectAddress}
          handleDob={handleSelectDob}
          isAssociated={true}
        />
      )}

      {fields?.map((field, index) => (
        <div key={field.id}>
          <div className="text-gray-600 font-semibold text-base flex items-center justify-between w-full">
            Person #{index + 1}
            <DeleteButton onClick={() => handleRemoveIndividual(index)} />
          </div>

          <AddNewIndividualForm
            control={control}
            errors={errors}
            relationshipOptions={filteredRoles}
            formIndex={index}
            setValue={setValue}
            fieldGroup='newAssociatedParty'
          />
        </div>
      ))}

      <BaseButton type="link" className="link-button" onClick={handleAddNewIndividual}>
        <PlusSquare width={15} height={15} />
        Add Associated Party
      </BaseButton>
    </>
  );
};
